<script setup lang="ts">
import { useAddressSystemStore } from '@/store/addressSystem'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { BalloonType } from '@/store/delivery/delivery.types'

import BalloonDelivery from '@/components/Services/Balloons/BalloonDelivery.vue'
import BalloonTable from '@/components/Services/Balloons/BalloonTable.vue'
import BalloonTitle from '@/components/Services/Balloons/BalloonTitle.vue'

import type { PolygonProperties } from '@/api/modules/delivery/types'

type Props = {
  details: PolygonProperties | null
  selectedAddress: DeliveryDataAddress | null
  balloonType?: BalloonType
  isLoading?: boolean
  clickable?: boolean
  balloonText?: string
}

type Emits = {
  (fn: 'onClick'): void
}

defineProps<Props>()

const emit = defineEmits<Emits>()

const { deliveryBasket } = storeToRefs(useAddressSystemStore())
</script>

<template>
  <div class="balloon-marker">
    <div class="balloon-marker__content">
      <template v-if="balloonType === BalloonType.Delivery">
        <balloon-title
          :is-loading="isLoading"
          :is-empty-info="!details"
          :selected-address="selectedAddress"
        />

        <balloon-table v-if="details" :data="details" />
      </template>

      <balloon-delivery
        v-if="balloonType === BalloonType.AddressSystem"
        :is-loading="isLoading"
        :selected-address="selectedAddress"
        :delivery-method="deliveryBasket.delivery_method"
        :clickable="clickable"
        :balloon-text="balloonText"
        @on-click="emit('onClick')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.balloon-marker {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  font-family: 'Raleways', sans-serif;
  font-weight: 500;

  &::before {
    position: absolute;
    bottom: 100%;
    width: 2px;
    height: 20px;
    content: '';
    background: var(--ui-secondary);
  }

  &__content {
    position: absolute;
    bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    min-width: 40px;
    height: auto;
    min-height: 40px;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    color: var(--ui-white);
    white-space: nowrap;
    background: var(--ui-secondary);
    border-radius: 20px;
    box-shadow: 0 4px 20px 0 rgba(65, 58, 91, 0.15);
  }
}
</style>
